import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Building2, Search, X, AlertTriangle, ChevronRight, UserCheck } from 'lucide-react';
import { toast } from 'react-toastify';
import { api } from '../services/api';
import { useReadOnly } from '../hooks/useReadOnly';
import { money } from '../utils/format';
import { daysUntil, termsLabel, TERMS_OPTIONS, dayLabel } from '../utils/commercial';
import ProposalPaste from '../components/ProposalPaste';
import AccountMark from '../components/AccountMark';

export interface CommercialRow {
  id: number; name: string; company_name: string | null; email: string | null; phone: string | null;
  account_mark?: 'P' | 'C' | null;
  billing_contact_name: string | null; billing_email: string | null; billing_phone: string | null;
  default_po_number: string | null; commercial_pricing: 'per_visit' | 'monthly';
  contract_monthly_amount: number | null; contract_start: string | null; contract_end: string | null;
  contract_auto_renew: number; service_reports_on: number; payment_terms: number | null;
  sites: number; open_balance: number; coi_expires: string | null; coi_count?: number; documents: number;
  last_report_at: string | null; reports_failed: number; next_visit: string | null;
}

/** What needs the office's eye on an account, in plain words. */
export function accountWarnings(a: CommercialRow): { text: string; bad: boolean }[] {
  const out: { text: string; bad: boolean }[] = [];
  const coi = daysUntil(a.coi_expires);
  if (!a.coi_count) out.push({ text: 'No COI on file', bad: false });
  else if (coi == null) { /* a COI with no expiry date on it: on file, nothing to warn about */ }
  else if (coi < 0) out.push({ text: `COI expired ${dayLabel(a.coi_expires)}`, bad: true });
  else if (coi <= 30) out.push({ text: `COI expires in ${coi} day${coi === 1 ? '' : 's'}`, bad: false });
  const end = daysUntil(a.contract_end);
  if (end != null && !a.contract_auto_renew) {
    if (end < 0) out.push({ text: `Contract ended ${dayLabel(a.contract_end)}`, bad: true });
    else if (end <= 30) out.push({ text: `Contract ends in ${end} day${end === 1 ? '' : 's'}`, bad: false });
  }
  if (a.reports_failed > 0) out.push({ text: `${a.reports_failed} report${a.reports_failed === 1 ? '' : 's'} failed`, bad: true });
  return out;
}

/** Two letters for the company's square — "AS" for American Self Storage. */
export const initials = (name: string) => {
  const words = String(name || '').replace(/[^A-Za-z0-9 ]/g, ' ').split(/\s+/).filter(Boolean);
  const skip = new Set(['the', 'of', 'and', 'llc', 'inc', 'co']);
  const picked = words.filter(w => !skip.has(w.toLowerCase())).slice(0, 2);
  return (picked.length ? picked : words.slice(0, 2)).map(w => w[0].toUpperCase()).join('') || 'B';
};

export const pricingLabel = (a: { commercial_pricing: string; contract_monthly_amount: number | null }) =>
  a.commercial_pricing === 'monthly' && a.contract_monthly_amount
    ? `${money(a.contract_monthly_amount)}/mo contract`
    : 'Per visit';

export default function CommercialAccounts() {
  const navigate = useNavigate();
  const readOnly = useReadOnly();
  const [showNew, setShowNew] = useState(false);
  const [showMark, setShowMark] = useState(false);
  const [showPaste, setShowPaste] = useState(false);   // a proposal pasted in as written (owner, Sep 28 2026)

  const { data, isLoading, isError } = useQuery(['commercial'], () =>
    api.get('/commercial').then(r => r.data.accounts as CommercialRow[]));
  const accounts = data || [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-black tracking-tight text-gray-900">Commercial accounts</h1>
          <p className="text-sm text-gray-600">
            {accounts.length ? `${accounts.length} account${accounts.length === 1 ? '' : 's'} · ${accounts.reduce((t, a) => t + a.sites, 0)} sites · ${money(accounts.reduce((t, a) => t + Number(a.open_balance || 0), 0))} open` : 'Business customers. Their visits run on the normal routes and crew app.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setShowMark(true)} disabled={readOnly} className="btn-secondary">
            <UserCheck className="h-4 w-4" /> Existing customer
          </button>
          <button onClick={() => setShowNew(true)} disabled={readOnly} className="btn-primary">
            <Plus className="h-4 w-4" /> New account
          </button>
          <button onClick={() => setShowPaste(true)} disabled={readOnly} className="btn-secondary text-sm disabled:opacity-50">
            Paste a proposal
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="card p-8 text-center text-gray-500">Loading…</div>
      ) : isError ? (
        <div className="card p-8 text-center text-red-600">Could not load commercial accounts.</div>
      ) : accounts.length === 0 ? (
        <div className="card p-8 text-center text-gray-600">
          <Building2 className="h-10 w-10 mx-auto text-gray-400 mb-2" />
          <p className="font-semibold">No commercial accounts yet.</p>
          <p className="text-sm mt-1">Add a new one, or turn an existing customer into one.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {accounts.map(a => {
            const warns = accountWarnings(a);
            const contact = a.name && a.name !== a.company_name ? a.name : null;
            return (
              <button key={a.id} onClick={() => navigate(`/commercial/${a.id}`)}
                className="card w-full text-left p-4 hover:border-brand-300 hover:shadow-lift transition">
                <div className="flex items-start gap-3">
                  <div className="h-12 w-12 rounded-2xl bg-gray-900 text-white flex items-center justify-center text-base font-black tracking-tight flex-shrink-0">
                    {initials(a.company_name || a.name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-black text-gray-900 truncate text-lg leading-tight">{a.company_name || a.name}<AccountMark mark={a.account_mark} /></p>
                    <p className="text-sm text-gray-600 truncate">
                      {[contact, a.billing_email || a.email].filter(Boolean).join(' · ') || 'No contact yet'}
                    </p>
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-5 gap-x-4 gap-y-1 text-sm">
                      <Stat label="Sites" value={String(a.sites)} />
                      <Stat label="Pricing" value={pricingLabel(a)} />
                      <Stat label="Terms" value={termsLabel(a.payment_terms)} />
                      <Stat label="Open balance" value={money(a.open_balance)} strong={a.open_balance > 0} />
                      <Stat label="Next visit" value={a.next_visit ? dayLabel(a.next_visit) : '—'} />
                    </div>
                    {warns.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {warns.map(w => (
                          <span key={w.text} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border ${
                            w.bad ? 'bg-red-50 text-red-800 border-red-200' : 'bg-amber-50 text-amber-800 border-amber-300'}`}>
                            <AlertTriangle className="h-3 w-3" /> {w.text}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 mt-1" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {showNew && <NewAccountModal onClose={() => setShowNew(false)} onMade={id => navigate(`/commercial/${id}`)} />}
      {showMark && <MarkExistingModal onClose={() => setShowMark(false)} onMade={id => navigate(`/commercial/${id}`)} />}
      {showPaste && <ProposalPaste onClose={() => setShowPaste(false)} />}
    </div>
  );
}

function Stat({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`truncate ${strong ? 'font-black text-gray-900' : 'text-gray-800'}`}>{value}</p>
    </div>
  );
}

export function Modal({ title, onClose, children, footer }: {
  title: string; onClose: () => void; children: React.ReactNode; footer?: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg max-h-[92vh] flex flex-col rounded-t-3xl sm:rounded-3xl shadow-lift" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-gray-200">
          <h2 className="font-black text-lg text-gray-900">{title}</h2>
          <button onClick={onClose} aria-label="Close" className="h-9 w-9 flex items-center justify-center rounded-2xl text-gray-600 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto space-y-3">{children}</div>
        {footer && <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

export function Field({ label, children, className = '' }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={className}>
      <label className="label-sm">{label}</label>
      {children}
    </div>
  );
}

const emptyNew = {
  company_name: '', name: '', email: '', phone: '',
  billing_contact_name: '', billing_email: '', default_po_number: '',
  payment_terms: 30, commercial_pricing: 'per_visit' as 'per_visit' | 'monthly', contract_monthly_amount: '',
  contract_start: '', contract_end: '', contract_auto_renew: false,
  site_address: '', city: '', zip_code: '', site_label: '',
  site_contact_name: '', site_contact_phone: '', site_contact_email: '',
};

function NewAccountModal({ onClose, onMade }: { onClose: () => void; onMade: (id: number) => void }) {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const [f, setF] = useState(emptyNew);
  const set = (k: keyof typeof emptyNew) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF(prev => ({ ...prev, [k]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value }));

  const save = useMutation(() => {
    const body: any = {};
    for (const [k, v] of Object.entries(f)) {
      if (typeof v === 'string') { if (v.trim()) body[k] = v.trim(); }
      else body[k] = v;
    }
    body.payment_terms = Number(f.payment_terms);
    if (f.commercial_pricing === 'monthly') body.contract_monthly_amount = Number(f.contract_monthly_amount) || 0;
    else delete body.contract_monthly_amount;
    return api.post('/commercial', body).then(r => r.data);
  }, {
    onSuccess: (d: any) => {
      toast.success(d?.message || 'Account added');
      qc.invalidateQueries(['commercial']);
      onClose();
      if (d?.id) onMade(d.id);
    },
    onError: (e: any) => { if (!e?.isReadOnlyBlock) toast.error(e?.response?.data?.message || 'Could not add the account'); },
  });

  const monthly = f.commercial_pricing === 'monthly';
  const canSave = f.company_name.trim() && (!monthly || Number(f.contract_monthly_amount) > 0);

  return (
    <Modal title="New commercial account" onClose={onClose} footer={<>
      <button onClick={onClose} className="btn-secondary">Cancel</button>
      <button onClick={() => save.mutate()} disabled={readOnly || !canSave || save.isLoading} className="btn-primary">
        {save.isLoading ? 'Saving…' : 'Add account'}
      </button>
    </>}>
      <Field label="Company *"><input value={f.company_name} onChange={set('company_name')} className="input" placeholder="Acme Property Mgmt" /></Field>

      <p className="text-xs font-black uppercase tracking-wide text-gray-700 pt-1">Main contact</p>
      <Field label="Name"><input value={f.name} onChange={set('name')} className="input" /></Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Email"><input type="email" value={f.email} onChange={set('email')} className="input" /></Field>
        <Field label="Phone"><input value={f.phone} onChange={set('phone')} className="input" /></Field>
      </div>

      <p className="text-xs font-black uppercase tracking-wide text-gray-700 pt-1">Billing</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="AP contact"><input value={f.billing_contact_name} onChange={set('billing_contact_name')} className="input" /></Field>
        <Field label="AP emails — every bill goes to all"><input inputMode="email" value={f.billing_email} onChange={set('billing_email')} className="input" placeholder="ap@company.com, owner@company.com" /></Field>
        <Field label="PO number"><input value={f.default_po_number} onChange={set('default_po_number')} className="input" /></Field>
        <Field label="Terms">
          <select value={f.payment_terms} onChange={set('payment_terms')} className="input">
            {TERMS_OPTIONS.map(t => <option key={t} value={t}>{termsLabel(t)}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Pricing">
        <select value={f.commercial_pricing} onChange={set('commercial_pricing')} className="input">
          <option value="per_visit">Per visit</option>
          <option value="monthly">Flat monthly contract</option>
        </select>
      </Field>
      {monthly && (
        <Field label="Monthly amount *">
          <input type="number" inputMode="decimal" min="0" step="0.01" value={f.contract_monthly_amount} onChange={set('contract_monthly_amount')} className="input" placeholder="0.00" />
        </Field>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Contract start"><input type="date" value={f.contract_start} onChange={set('contract_start')} className="input" /></Field>
        <Field label="Contract end"><input type="date" value={f.contract_end} onChange={set('contract_end')} className="input" /></Field>
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-800">
        <input type="checkbox" checked={f.contract_auto_renew} onChange={set('contract_auto_renew')} className="h-4 w-4" /> Auto-renews
      </label>

      <p className="text-xs font-black uppercase tracking-wide text-gray-700 pt-1">First site (optional)</p>
      <Field label="Address"><input value={f.site_address} onChange={set('site_address')} className="input" placeholder="123 Main St" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="City"><input value={f.city} onChange={set('city')} className="input" /></Field>
        <Field label="Zip"><input value={f.zip_code} onChange={set('zip_code')} className="input" /></Field>
      </div>
      <Field label="Site name"><input value={f.site_label} onChange={set('site_label')} className="input" placeholder="e.g. North office" /></Field>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Site contact"><input value={f.site_contact_name} onChange={set('site_contact_name')} className="input" /></Field>
        <Field label="Site phone"><input value={f.site_contact_phone} onChange={set('site_contact_phone')} className="input" /></Field>
        <Field label="Site emails"><input inputMode="email" value={f.site_contact_email} onChange={set('site_contact_email')} className="input" placeholder="one, or several with commas" /></Field>
      </div>
    </Modal>
  );
}

function MarkExistingModal({ onClose, onMade }: { onClose: () => void; onMade: (id: number) => void }) {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const [q, setQ] = useState('');
  const term = q.trim();
  const { data, isFetching } = useQuery(['commercial', 'candidates', term], () =>
    api.get('/commercial/candidates', { params: { q: term } }).then(r => r.data.customers as any[]),
    { enabled: term.length >= 2, keepPreviousData: true });

  const mark = useMutation((c: any) =>
    api.post(`/commercial/${c.id}/mark`, { on: true, company_name: c.company_name || undefined }).then(r => r.data), {
    onSuccess: (d: any) => {
      toast.success(d?.message || 'Now a commercial account');
      qc.invalidateQueries(['commercial']);
      onClose();
      if (d?.id) onMade(d.id);
    },
    onError: (e: any) => { if (!e?.isReadOnlyBlock) toast.error(e?.response?.data?.message || 'Could not change the account'); },
  });

  const rows = term.length >= 2 ? (data || []) : [];
  return (
    <Modal title="Make a customer commercial" onClose={onClose}>
      <p className="text-sm text-gray-600">Their sites, schedule and history stay as they are.</p>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input autoFocus value={q} onChange={e => setQ(e.target.value)} className="input pl-10" placeholder="Name, company, email or address" />
      </div>
      {term.length < 2 ? (
        <p className="text-sm text-gray-500">Type at least 2 letters.</p>
      ) : isFetching && !data ? (
        <p className="text-sm text-gray-500">Searching…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">No matching customers.</p>
      ) : (
        <ul className="divide-y divide-gray-200 border border-gray-200 rounded-2xl">
          {rows.map(c => (
            <li key={c.id} className="flex items-center gap-3 px-3 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-gray-900 truncate">{c.company_name || c.name}<AccountMark mark={c.account_mark} /></p>
                <p className="text-xs text-gray-600 truncate">
                  {[c.company_name ? c.name : null, c.email || c.phone, `${c.sites} site${c.sites === 1 ? '' : 's'}`].filter(Boolean).join(' · ')}
                </p>
              </div>
              <button onClick={() => mark.mutate(c)} disabled={readOnly || mark.isLoading} className="btn-primary text-sm">
                Make commercial
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
